import { useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as DownloadIcon } from '@elementary/common-ui/assets/download.svg';
import {
  FlexColumn,
  FlexSpace,
  Button,
  FlexCenter,
  TestLineageNavButton,
} from '@elementary/common-ui/components';
import { Typography } from '@mui/material';
import DependenciesTable from 'shared/components/DependenciesTable';
import { DependenciesFilterOptions } from 'shared/components/DependenciesTable/types';
import PanelError from 'shared/components/PanelError';
import {
  fontSize,
  fontWeight,
  radius,
  space,
} from '@elementary/common-ui/style/utils';
import useDependenciesData from '../hooks/useDependenciesData';
import DependenciesEmptyState from './DependenciesEmptyState';

const Wrapper = styled(FlexColumn)`
  background-color: ${({ theme }) => theme.palette.surface.soft};
  padding: ${space('lg', 'xxl')};
  gap: ${space('lg')};
  border-radius: ${radius('lg')};
  height: 100%;
  justify-content: center;
`;

type Props = {
  tableAssetId: string;
  testName: string;
  resource: {
    type: string;
    name: string;
  };
};

const Dependencies = ({ tableAssetId, testName, resource }: Props) => {
  const [filter, setFilter] = useState<DependenciesFilterOptions>(
    DependenciesFilterOptions.All,
  );
  const [includeLatestExecution, setIncludeLatestExecution] = useState(false);

  const { data, isLoading, onCSVDownload, getColumnName, error, isNotFound } =
    useDependenciesData({
      tableAssetId,
      resource,
      filter,
      includeLatestExecution,
    });
  const columnName = getColumnName(resource.name);

  if (isNotFound) {
    return (
      <DependenciesEmptyState
        title="Dependencies aren't available"
        description={
          resource.type === 'COLUMN'
            ? `Column-level metadata isn't synced for this table, so the dependencies of "${columnName}" can't be resolved.`
            : "This asset's metadata isn't synced, so its dependencies can't be resolved."
        }
      />
    );
  }

  return (
    <Wrapper>
      {error ? (
        <PanelError
          error={error as Error}
          errorName="ApiError"
          page={'Dependencies'}
        />
      ) : (
        <>
          <FlexSpace sx={{ alignItems: 'center' }}>
            <Typography
              sx={{
                fontSize: fontSize('lg'),
                fontWeight: fontWeight('medium'),
              }}
            >
              Related Assets
            </Typography>
            <FlexCenter sx={{ gap: 1 }}>
              <TestLineageNavButton
                modelId={tableAssetId}
                testName={testName}
                columnName={resource.type === 'COLUMN' ? columnName : undefined}
                buttonStyle="button"
                disabled={isLoading}
              >
                Show in lineage
              </TestLineageNavButton>
              <Button
                variant="outlined"
                onClick={onCSVDownload}
                startIcon={<DownloadIcon width={12} />}
                disabled={isLoading}
              >
                CSV
              </Button>
            </FlexCenter>
          </FlexSpace>
          <DependenciesTable
            modelId={tableAssetId}
            data={data}
            loading={isLoading}
            resource={resource}
            filter={filter}
            setFilter={(f) => {
              setFilter(f as DependenciesFilterOptions);
            }}
            onShowLatestExecution={(val) => setIncludeLatestExecution(val)}
          />
        </>
      )}
    </Wrapper>
  );
};

export default Dependencies;
